import React, { FC } from "react";
import { Avatar, Tooltip } from "antd";
// RightOutlined is the inline chevron on the CTA links; the app set has no
// text-sized chevron, only the full-size next-icon arrow.
import { UserOutlined, RightOutlined } from "@ant-design/icons";
import moment from "moment";
import { MessageTypes } from "constants/enums";
import BeamIcon, { fileIconSrc } from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * Compact in-stream bodies for each message type.
 *
 * These are the counterpart to the expanded cards: a message in the stream is
 * one item at one timestamp, not a ledger. MessageItem already renders the
 * chrome (avatar, username, time, type icon) and MessageBox renders the body —
 * so these are bodies only, sized for the ~380px usable width of the centre
 * column.
 *
 * Drop-in: replace the per-type `sub-text` blocks in MessageBox with
 * <MessageTypeBody message={message} />.
 */

interface BodyProps {
  message: any;
  onOpen?: (message: any) => void;
}

/**
 * AddPayModal stores the currency *symbol* ("$"), not an ISO code, and
 * Intl.NumberFormat throws a RangeError on anything that is not a valid code.
 */
const CURRENCY_CODES: Record<string, string> = {
  $: "USD",
  "£": "GBP",
  "€": "EUR",
};

const formatAmount = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: CURRENCY_CODES[currency] || currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Unknown currency: show the symbol as given rather than throwing.
    return `${currency}${amount.toFixed(2)}`;
  }
};

/**
 * Message payloads come from the server and from modals written long before
 * these bodies, so field shapes vary. An uncaught throw in render blanks the
 * entire dashboard — one odd message must never cost the whole stream.
 */
class BodyBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.error("MessageTypeBody failed to render", error);
  }

  render() {
    // Falling back to the plain text body above is better than an error strip:
    // the message is still readable, just without its rich rendering.
    return this.state.failed ? null : this.props.children;
  }
}

/* ---------------- Pay ---------------- */

const PayBody: FC<BodyProps> = ({ message, onOpen }) => {
  const paid = message.data?.status === "succeeded";
  const amount = Number(message.data?.amount ?? 0);
  const currency = message.data?.currency || "USD";

  return (
    <div className="mt-body mt-body--pay">
      <span className="mt-body__amount">{formatAmount(amount, currency)}</span>
      <span className={`mt-pill ${paid ? "mt-pill--settled" : "mt-pill--pending"}`}>
        {paid ? "Paid" : "Unpaid"}
      </span>
      {message.data?.method && (
        <span className="mt-body__muted">{message.data.method}</span>
      )}
      {!paid && (
        <button
          type="button"
          className="mt-body__cta"
          onClick={() => onOpen?.(message)}
        >
          Pay now <RightOutlined />
        </button>
      )}
    </div>
  );
};

/* ---------------- Event ---------------- */

/**
 * AddEventModal collapses the date and time pickers into a single `dateTime`
 * epoch before sending, and stores the place as an object rather than a string —
 * rendering that object directly would throw, so only its address is read.
 */
const eventLocationText = (location: any): string | undefined => {
  if (!location) return undefined;
  if (typeof location === "string") return location;
  return location.attributes?.address || location.attributes?.title;
};

const EventBody: FC<BodyProps> = ({ message, onOpen }) => {
  const start = message.data?.dateTime ?? message.data?.start;
  const end = message.data?.end;
  const location = eventLocationText(message.data?.location);

  return (
    <button
      type="button"
      className="mt-body mt-body--event"
      onClick={() => onOpen?.(message)}
    >
      {/* moment(undefined) is *now*, so an event saved without a date would
          silently show today rather than admitting it has none. */}
      <span className="mt-body__datechip">
        <b>{start ? moment(start).format("D") : "–"}</b>
        <em>{start ? moment(start).format("MMM") : ""}</em>
      </span>

      <span className="mt-body__event-meta">
        <b>{message.data?.title || message.message}</b>
        <em>
          {start ? moment(start).format("h:mm A") : "No date set"}
          {start && end ? ` – ${moment(end).format("h:mm A")}` : ""}
        </em>
        {location && (
          <em className="mt-body__muted">
            <BeamIcon name="location" size={11} /> {location}
          </em>
        )}
      </span>

      {message.data?.thumbnail && (
        <img className="mt-body__thumb" src={message.data.thumbnail} alt="" />
      )}
    </button>
  );
};

/* ---------------- RSVP ---------------- */

/**
 * AddRSVPModal stores the three custom button labels, not tallies — the replies
 * arrive later as separate `rsvp-response` messages keyed by `responseTo`.
 * A single stream item cannot see those, so the split bar only renders when the
 * payload actually carries counts; otherwise the options themselves are shown,
 * which is what the recipient has to act on anyway.
 */
const RsvpBody: FC<BodyProps> = ({ message, onOpen }) => {
  const options: string[] = [
    message.data?.options?.option1,
    message.data?.options?.option2,
    message.data?.options?.option3,
  ].filter(Boolean);

  const hasCounts =
    typeof message.data?.yes === "number" ||
    typeof message.data?.maybe === "number" ||
    typeof message.data?.no === "number";

  if (!hasCounts) {
    return (
      <div className="mt-body mt-body--rsvp">
        {options.length > 0 && (
          <span className="mt-body__rsvp-options">
            {options.map((option) => (
              <em key={option}>{option}</em>
            ))}
          </span>
        )}
        <button
          type="button"
          className="mt-body__cta"
          onClick={() => onOpen?.(message)}
        >
          Respond <RightOutlined />
        </button>
      </div>
    );
  }

  const yes = message.data?.yes ?? 0;
  const maybe = message.data?.maybe ?? 0;
  const no = message.data?.no ?? 0;
  const total = yes + maybe + no || 1;
  const pct = (value: number) => `${(value / total) * 100}%`;

  return (
    <div className="mt-body mt-body--rsvp">
      {/* One stacked bar instead of three rings: at this width a bar carries
          the same split and leaves room for the counts beside it. */}
      <span className="mt-body__bar">
        <i className="is-yes" style={{ width: pct(yes) }} />
        <i className="is-maybe" style={{ width: pct(maybe) }} />
        <i className="is-no" style={{ width: pct(no) }} />
      </span>

      <span className="mt-body__rsvp-counts">
        <b className="is-yes">{yes} yes</b>
        <b className="is-maybe">{maybe} maybe</b>
        <b className="is-no">{no} no</b>
      </span>

      <button
        type="button"
        className="mt-body__cta"
        onClick={() => onOpen?.(message)}
      >
        Respond <RightOutlined />
      </button>
    </div>
  );
};

/* ---------------- File ---------------- */

const FileBody: FC<BodyProps> = ({ message, onOpen }) => {
  const name = message.data?.name || message.message;

  return (
    <button
      type="button"
      className="mt-body mt-body--file"
      onClick={() => onOpen?.(message)}
    >
      <img className="mt-body__ext" src={fileIconSrc(name)} alt="" />
      <span className="mt-body__file-meta">
        <b>{name}</b>
        <em>
          {message.data?.sizeLabel}
          {message.data?.version ? ` · ${message.data.version}` : ""}
        </em>
      </span>
    </button>
  );
};

/* ---------------- Alert ---------------- */

const SEVERITY_COLORS: Record<string, string> = {
  critical: "#FF5F25",
  warning: "#FBAE16",
  info: "#508afa",
};

/**
 * IoTAlertModal saves a *rule* (name + speed/stop thresholds), while the
 * backend pushes fired alerts with type/description. One body serves both: the
 * rule's thresholds stand in for the description it does not carry.
 */
const alertRuleSummary = (data: any): string | undefined => {
  if (!data) return undefined;
  const parts: string[] = [];
  if (data.lowerSpeedThreshold || data.upperSpeedThreshold) {
    parts.push(
      `Speed ${data.lowerSpeedThreshold ?? "–"}–${data.upperSpeedThreshold ?? "–"} mph`
    );
  }
  if (data.speedingTime) parts.push(`over ${data.speedingTime} min`);
  if (data.stopTime) parts.push(`stopped ${data.stopTime} min`);
  return parts.length ? parts.join(" · ") : undefined;
};

const AlertBody: FC<BodyProps> = ({ message, onOpen }) => {
  const severity = message.data?.severity || "info";
  // Fired alerts carry occurredAt; a saved rule only has the message timestamp.
  const at = message.data?.occurredAt ?? message.createdAt;

  return (
    <div
      className="mt-body mt-body--alert"
      style={{ borderLeftColor: SEVERITY_COLORS[severity] }}
    >
      <span className="mt-body__alert-head">
        <i
          className="mt-body__sev-dot"
          style={{ background: SEVERITY_COLORS[severity] }}
        />
        <b>{message.data?.type || message.data?.name || "Alert"}</b>
        {at && <em>{moment(at).fromNow()}</em>}
      </span>

      <span className="mt-body__alert-row">
        <p>
          {message.data?.description ||
            alertRuleSummary(message.data) ||
            message.message}
        </p>
        {message.data?.artifact && (
          <button
            type="button"
            className="mt-body__artifact"
            onClick={() => onOpen?.(message)}
          >
            <img src={message.data.artifact.url} alt="" />
            {message.data.artifact.kind === "video" && (
              <BeamIcon name="video" size={18} />
            )}
          </button>
        )}
      </span>
    </div>
  );
};

/* ---------------- People ---------------- */

const PeopleBody: FC<BodyProps> = ({ message, onOpen }) => {
  const people: any[] = message.data?.people || [];
  const shown = people.slice(0, 3);
  const rest = people.length - shown.length;

  return (
    <button
      type="button"
      className="mt-body mt-body--people"
      onClick={() => onOpen?.(message)}
    >
      <Avatar.Group maxCount={3} size={26}>
        {shown.map((person) => (
          <Tooltip key={person.id} title={person.name}>
            <Avatar src={person.avatar} icon={<UserOutlined />} />
          </Tooltip>
        ))}
      </Avatar.Group>
      <span className="mt-body__people-meta">
        <b>{shown.map((p) => p.name).join(", ")}</b>
        {rest > 0 && <em>+{rest} more</em>}
      </span>
    </button>
  );
};

/* ---------------- Survey ---------------- */

/**
 * Surveys are authored in the shared survey app, so the message often carries
 * only a surveyId and a link. Response totals live in that app — printing
 * "0 responses" when the field is simply absent would misreport a live survey.
 */
const SurveyBody: FC<BodyProps> = ({ message, onOpen }) => {
  const responses = message.data?.responses;
  const completion = message.data?.completionRate;

  return (
    <button
      type="button"
      className="mt-body mt-body--survey"
      onClick={() => onOpen?.(message)}
    >
      <span className="mt-body__survey-meta">
        <b>{message.data?.title || message.message}</b>
        {typeof responses === "number" ? (
          <em>
            {responses} responses
            {typeof completion === "number"
              ? ` · ${completion}% complete`
              : ""}
          </em>
        ) : (
          <em className="mt-body__muted">Opens in the survey window</em>
        )}
      </span>
      <span className="mt-body__cta">
        Open <RightOutlined />
      </span>
    </button>
  );
};

/* ---------------- GPS report ---------------- */

/**
 * Two producers write this type: GPSReportModal sends reportingStart/EndDate,
 * while live tracking sends startIotAt/endIotAt. Both are read so a report
 * renders whichever way it was created. Point and distance totals are not in
 * the payload at all — those live in the track the map fetches on open.
 */
const GpsReportBody: FC<BodyProps> = ({ message, onOpen }) => {
  const start = message.data?.startIotAt ?? message.data?.reportingStartDate;
  const end = message.data?.endIotAt ?? message.data?.reportingEndDate;

  return (
    <button
      type="button"
      className="mt-body mt-body--gps"
      onClick={() => onOpen?.(message)}
    >
      <span className="mt-body__gps-meta">
        <b>{message.data?.title || message.data?.deviceKey || "GPS report"}</b>
        {start && (
          <em>
            {moment(start).format("D MMM, h:mm A")}
            {end ? ` – ${moment(end).format("h:mm A")}` : ""}
          </em>
        )}
      </span>
      <span className="mt-body__cta">
        View on map <RightOutlined />
      </span>
    </button>
  );
};

/* ---------------- Media ---------------- */

const MediaBody: FC<BodyProps> = ({ message, onOpen }) => {
  const mime: string = message.data?.mimeType || "";
  const url = message.data?.urls?.original?.url;

  if (mime.indexOf("image") !== -1) {
    return (
      <button
        type="button"
        className="mt-body mt-body--media"
        onClick={() => onOpen?.(message)}
      >
        <img src={url} alt="" />
      </button>
    );
  }

  // Non-image media gets a labelled tile — an <img> of a video URL renders
  // nothing, which is what the current MessageBox does today.
  const isVideo = mime.indexOf("video") !== -1;
  const isAudio = mime.indexOf("audio") !== -1;

  return (
    <button
      type="button"
      className="mt-body mt-body--media-file"
      onClick={() => onOpen?.(message)}
    >
      <span className="mt-body__media-icon">
        <BeamIcon
          name={isVideo ? "video" : isAudio ? "audio" : "file"}
          size={20}
        />
      </span>
      <span className="mt-body__media-meta">
        <b>{message.data?.name || (isVideo ? "Video" : isAudio ? "Audio" : "File")}</b>
        <em>{mime || "unknown type"}</em>
      </span>
    </button>
  );
};

/* ---------------- Place ---------------- */

/**
 * A shared location. The address is the whole point of the message, so it gets
 * the full width and wraps rather than being clipped to one line like the
 * secondary meta on other bodies.
 */
const PlaceBody: FC<BodyProps> = ({ message, onOpen }) => {
  const address = message.data?.attributes?.address;
  const name = message.data?.attributes?.name;

  return (
    <button
      type="button"
      className="mt-body mt-body--place"
      onClick={() => onOpen?.(message)}
    >
      <span className="mt-body__place-pin">
        <BeamIcon name="location" size={16} />
      </span>
      <span className="mt-body__place-meta">
        {name && <b>{name}</b>}
        <em>{address || "Location shared"}</em>
      </span>
      <span className="mt-body__cta">
        Open <RightOutlined />
      </span>
    </button>
  );
};

/* ---------------- GPS (live) ---------------- */

/**
 * Live tracking is a state, not an event — so this reads as a status strip
 * rather than a card you open. The dot is the signal an operator scans for.
 */
const GpsLiveBody: FC<BodyProps> = ({ message, onOpen }) => {
  // Anything other than an explicit off counts as live: the stream only carries
  // this message while tracking is running.
  const live = message.data?.status !== "off" && message.data?.live !== false;

  return (
    <div className="mt-body mt-body--gps-live">
      <span className={`mt-body__live ${live ? "is-on" : ""}`}>
        <i />
        {live ? "Live tracking on" : "Tracking stopped"}
      </span>
      {message.data?.deviceName && (
        <span className="mt-body__muted">{message.data.deviceName}</span>
      )}
      <button
        type="button"
        className="mt-body__cta"
        onClick={() => onOpen?.(message)}
      >
        View on map <RightOutlined />
      </button>
    </div>
  );
};

/* ---------------- Dispatcher ---------------- */

const renderBody = ({ message, onOpen }: BodyProps) => {
  switch (message.messageType) {
    case MessageTypes.Pay:
      return <PayBody message={message} onOpen={onOpen} />;
    case MessageTypes.Event:
      return <EventBody message={message} onOpen={onOpen} />;
    case MessageTypes.Rsvp:
      return <RsvpBody message={message} onOpen={onOpen} />;
    case MessageTypes.Alert:
    case MessageTypes.AlertConfiguration:
      return <AlertBody message={message} onOpen={onOpen} />;
    case MessageTypes.Survey:
      return <SurveyBody message={message} onOpen={onOpen} />;
    case MessageTypes.GPSReport:
      return <GpsReportBody message={message} onOpen={onOpen} />;
    case MessageTypes.Media:
      return <MediaBody message={message} onOpen={onOpen} />;
    case MessageTypes.Place:
      return <PlaceBody message={message} onOpen={onOpen} />;
    case MessageTypes.GPS:
      return <GpsLiveBody message={message} onOpen={onOpen} />;
    default:
      return null;
  }
};

const MessageTypeBody: FC<BodyProps> = ({ message, onOpen }) => (
  <BodyBoundary>{renderBody({ message, onOpen })}</BodyBoundary>
);

export {
  PayBody,
  EventBody,
  RsvpBody,
  FileBody,
  AlertBody,
  PeopleBody,
  SurveyBody,
  GpsReportBody,
  MediaBody,
  PlaceBody,
  GpsLiveBody,
};

export default MessageTypeBody;
